import React from "react";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useOrgConfig } from "src/hooks/useOrgConfig";
import * as Env from "src/services/env";
import { isNullOrUndefined } from "src/services/predicates";
import { SPECIAL_ROUTES } from "src/services/url/constants";
import * as RD from "src/types/remoteData";
import { useOrganizationPath } from "./useOrganizationPath";

type Auth0ClientId =
  | { type: "organization"; clientId: string }
  | { type: "default"; clientId: string };
export function useAuth0ClientId(): RD.RemoteData<never, Auth0ClientId> {
  const env = Env.read();
  const orgConfig = useOrgConfig<OrgConfig.Auth0Config>("Auth0");
  const orgPath = useOrganizationPath();

  return React.useMemo(() => {
    if (orgPath === undefined || SPECIAL_ROUTES.includes(orgPath))
      return RD.success<never, Auth0ClientId>({
        type: "default",
        clientId: env.VITE_AUTH0_CLIENT_ID,
      });

    return orgConfig
      .andThen<Auth0ClientId>((config) => {
        if (!isNullOrUndefined(config) && !config.disabled) {
          return RD.success({
            type: "organization",
            clientId: config.clientId,
          });
        }

        return RD.failure(
          new Error(
            `Missing clientId for ${orgPath}, most likely due to incomplete organization creation.`
          )
        );
      })
      .recover<never>((error) => {
        // IMPORTANT: Use Auth0's default clientId on any error to allow user to keep using the system.
        // The only known downside of using default clientId is that the user will recieve unbranded email when using password reset or resend email verification.
        console.error(error);
        return RD.success({
          type: "default",
          clientId: env.VITE_AUTH0_CLIENT_ID,
        });
      });
  }, [orgPath, orgConfig, env.VITE_AUTH0_CLIENT_ID]);
}
